import React from 'react';
import { Button, DotLoading, NavBar, PullToRefresh } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { campaignListPageStyles } from './styles';
import { CampaignItem, CampaignListState, CampaignUserPromotionStatus } from './types';

interface CampaignListViewProps {
  state: CampaignListState;
  onRefresh: () => Promise<void>;
}

export const CampaignListView: React.FC<CampaignListViewProps> = ({ state, onRefresh }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('campaigns');

  const formatDate = (value: string): string => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  const getPromotionStatus = (item: CampaignItem): CampaignUserPromotionStatus =>
    item.user_promotion_status || 'available';

  const getCardCopy = (item: CampaignItem): {
    statusLabel: string;
    statusTone: 'lavender' | 'indigo' | 'violet';
    scheduleText: string;
    footerHint: string;
    buttonText: string;
    canEnter: boolean;
  } => {
    const promotionStatus = getPromotionStatus(item);

    if (promotionStatus === 'completed') {
      return {
        statusLabel: t('list.statusCompleted'),
        statusTone: 'lavender',
        scheduleText: t('list.scheduleAlwaysOn'),
        footerHint: t('list.completedFooterHint'),
        buttonText: t('list.viewDetail'),
        canEnter: true,
      };
    }

    if (promotionStatus === 'quota_exhausted') {
      return {
        statusLabel: t('list.statusInProgress'),
        statusTone: 'indigo',
        scheduleText: t('list.scheduleDeadline', { date: formatDate(item.created_at) }),
        footerHint: t('list.exhaustedFooterHint'),
        buttonText: t('list.viewDetail'),
        canEnter: true,
      };
    }

    return {
      statusLabel: item.status === 'draft' ? t('list.statusComingSoon') : t('list.statusPending'),
      statusTone: item.status === 'draft' ? 'violet' : 'lavender',
      scheduleText: item.status === 'draft'
        ? t('list.scheduleAlwaysOn')
        : t('list.scheduleDeadline', { date: formatDate(item.created_at) }),
      footerHint: t('list.canForwardHint'),
      buttonText: t('list.goJoin'),
      canEnter: true,
    };
  };

  return (
    <div className="webview-page">
      <NavBar back={null}>{t('list.navTitle')}</NavBar>
      <div style={campaignListPageStyles.topDivider} />
      <div className="webview-section">
        <div style={campaignListPageStyles.introCard}>
          <div style={campaignListPageStyles.introTopRow}>
            <div style={campaignListPageStyles.centerCaption}>
              <span style={campaignListPageStyles.centerCaptionBar} />
              <span>{t('list.centerCaption')}</span>
            </div>
            <div style={campaignListPageStyles.introCountPill}>
              {t('list.totalCount', { value: state.items.length })}
            </div>
          </div>
          <div style={campaignListPageStyles.introTitle}>{t('list.title')}</div>
          <div style={campaignListPageStyles.introSubtitleRow}>
            <span style={campaignListPageStyles.introSubtitleIcon}>
              <ClockGlyph />
            </span>
            <span style={campaignListPageStyles.introSubtitle}>{t('list.subtitle')}</span>
          </div>
        </div>
      </div>
      <PullToRefresh onRefresh={onRefresh} headHeight={0}>
        <div className="webview-section" style={{ marginTop: 10 }}>
          {state.loading ? (
            <div className="webview-card" style={{ textAlign: 'center', padding: '40px 0' }}>
              <DotLoading />
            </div>
          ) : null}
          {!state.loading && state.items.length === 0 ? (
            <div className="webview-card">
              <div className="webview-empty" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1b1d24' }}>{t('list.emptyTitle')}</div>
                <div className="webview-micro" style={{ marginTop: 8 }}>
                  {t('list.emptyHint')}
                </div>
              </div>
            </div>
          ) : null}
          {!state.loading && state.items.map((item) => {
            const cardCopy = getCardCopy(item);

            return (
              <div key={item.id} className="webview-card" style={{ ...campaignListPageStyles.campaignCard, marginBottom: 14 }}>
                <div style={campaignListPageStyles.cardMeta}>
                  <div style={campaignListPageStyles.titleBlock}>
                    <div style={campaignListPageStyles.campaignTitle}>{item.name}</div>
                  </div>
                  <div
                    style={{
                      ...campaignListPageStyles.statusPill,
                      ...(cardCopy.statusTone === 'indigo'
                        ? campaignListPageStyles.statusPillIndigo
                        : cardCopy.statusTone === 'violet'
                          ? campaignListPageStyles.statusPillViolet
                          : campaignListPageStyles.statusPillLavender),
                    }}
                  >
                    {cardCopy.statusLabel}
                  </div>
                </div>

                <div style={campaignListPageStyles.metaGroup}>
                  <div style={campaignListPageStyles.metaRow}>
                    <span style={campaignListPageStyles.metaIcon}><ClockGlyph /></span>
                    <span>{cardCopy.scheduleText}</span>
                  </div>
                  <div style={campaignListPageStyles.metaRow}>
                    <span style={campaignListPageStyles.metaIcon}><HashGlyph /></span>
                    <span>#{item.id.slice(0, 8)}</span>
                  </div>
                </div>

                <div style={campaignListPageStyles.descriptionBox}>
                  <div style={campaignListPageStyles.description}>
                    {item.description || t('list.noDescription')}
                  </div>
                </div>

                <div style={campaignListPageStyles.footer}>
                  <div style={campaignListPageStyles.footerHint}>
                    {cardCopy.footerHint}
                  </div>
                  <Button
                    color="primary"
                    disabled={!cardCopy.canEnter}
                    style={campaignListPageStyles.primaryButton}
                    onClick={() => navigate(`/campaigns/${item.id}`)}
                  >
                    {cardCopy.buttonText}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </PullToRefresh>
    </div>
  );
};

const ClockGlyph: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.7" />
    <path d="M10 6.7v3.7l2.45 1.45" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const HashGlyph: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M7.2 4.5 5.9 15.5M14.1 4.5l-1.3 11M4.1 8.2h12M3.4 11.8h12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
